import { useMemo, useState, type FormEvent } from 'react';
import { Button, FileInput, Stack, Text, Title } from '@mantine/core';
import type { AxiosError } from 'axios';
import { Icon } from '@/components';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import { useCaminoMemberImports, useStartCaminoMemberImport } from '@/hooks/organization-programs';
import { CONTROL_SIZES } from '@/shared/utils/theme.utils';
import { isCaminoMemberImportInProgress } from '@/types';
import type { ErrorResponse } from '@/types/error';
import styles from './CaminoMemberImportForm.module.sass';

const LAYOUT = {
  UPLOAD_ICON_SIZE: 16,
};

const MAX_CSV_SIZE_BYTES = 5 * 1024 * 1024;

const FILE_INPUT_STYLES = {
  root: {
    gap: 0,
  },
  wrapper: {
    '--input-height-xs': CONTROL_SIZES.xs,
    '--input-height-sm': CONTROL_SIZES.sm,
    '--input-height-md': CONTROL_SIZES.md,
    '--input-height-lg': CONTROL_SIZES.lg,
    '--input-height': CONTROL_SIZES.md,
    height: CONTROL_SIZES.md,
    minHeight: CONTROL_SIZES.md,
  },
  input: {
    height: CONTROL_SIZES.md,
    minHeight: CONTROL_SIZES.md,
  },
} as const;

const CONTENT = {
  TITLE: 'Member Import',
  FILE_PLACEHOLDER: 'Select a CSV file',
  IMPORT_BUTTON: 'Import',
  ACTIVE_IMPORT_HINT: 'An import is already in progress for this church.',
  FILE_REQUIRED_ERROR: 'Select a CSV file to import.',
  FILE_TOO_LARGE_ERROR: 'The CSV file must be 5 MiB or smaller.',
  FILE_TYPE_ERROR: 'Only CSV files are supported.',
};

type CaminoMemberImportFormProps = {
  churchId: string;
};

const isCsvFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase();
  return lowerName.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
};

const CaminoMemberImportForm = ({ churchId }: CaminoMemberImportFormProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);

  const { data: imports = [] } = useCaminoMemberImports(churchId);
  const startImportMutation = useStartCaminoMemberImport(churchId);

  const hasActiveImport = useMemo(
    () => imports.some((importRun) => isCaminoMemberImportInProgress(importRun.status)),
    [imports]
  );

  const isSubmitDisabled =
    hasActiveImport || startImportMutation.isPending || selectedFile === null;

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
    setClientError(null);
    startImportMutation.reset();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (hasActiveImport || startImportMutation.isPending) {
      return;
    }

    if (!selectedFile) {
      setClientError(CONTENT.FILE_REQUIRED_ERROR);
      return;
    }

    if (!isCsvFile(selectedFile)) {
      setClientError(CONTENT.FILE_TYPE_ERROR);
      return;
    }

    if (selectedFile.size > MAX_CSV_SIZE_BYTES) {
      setClientError(CONTENT.FILE_TOO_LARGE_ERROR);
      return;
    }

    setClientError(null);
    startImportMutation.mutate(selectedFile, {
      onSuccess: () => {
        setSelectedFile(null);
      },
    });
  };

  const apiError = (startImportMutation.error as AxiosError<ErrorResponse> | null) ?? null;

  return (
    <Stack gap="md">
      <Title order={4}>{CONTENT.TITLE}</Title>

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <div className={styles.fileRow}>
            <FileInput
              size="md"
              placeholder={CONTENT.FILE_PLACEHOLDER}
              accept=".csv,text/csv"
              clearable
              value={selectedFile}
              onChange={handleFileChange}
              disabled={hasActiveImport || startImportMutation.isPending}
              leftSection={<Icon name="IconUpload" size={LAYOUT.UPLOAD_ICON_SIZE} />}
              className={styles.fileInput}
              styles={FILE_INPUT_STYLES}
            />
            <Button
              type="submit"
              size="md"
              loading={startImportMutation.isPending}
              disabled={isSubmitDisabled}
              className={styles.importButton}
            >
              {CONTENT.IMPORT_BUTTON}
            </Button>
          </div>

          {clientError && (
            <Text size="sm" c="red">
              {clientError}
            </Text>
          )}

          {hasActiveImport && (
            <Text size="sm" c="dimmed">
              {CONTENT.ACTIVE_IMPORT_HINT}
            </Text>
          )}

          <SaveErrorBox error={apiError} />
        </Stack>
      </form>
    </Stack>
  );
};

export default CaminoMemberImportForm;
